import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../components/Icon';
import { PatientAvatarImage } from '../../../components/PatientAvatarImage';
import type { Patient } from '../../../types/admin';
import { useUpdatePatient } from '../hooks/usePatientMutations';
import styles from './PatientProfile.module.scss';

type PatientProfileProps = {
  readonly patient: Patient;
  readonly actions?: ReactNode;
};

export function PatientProfile({ patient, actions }: PatientProfileProps) {
  const { t, i18n } = useTranslation();
  const initials = `${patient.first_name[0]}${patient.last_name[0]}`.toUpperCase();

  function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  }

  function formatGender(value: string | null | undefined): string {
    if (!value) return '—';
    const labels: Record<string, string> = {
      male: t('patientProfile.genderMale'),
      female: t('patientProfile.genderFemale'),
      other: t('patientProfile.genderOther'),
    };
    return labels[value.toLowerCase()] ?? value;
  }

  return (
    <header className={styles.header}>
      <div className={styles.identity}>
        <div className={styles.avatar}>
          {patient.avatar_url ? (
            <PatientAvatarImage
              patientId={patient.id}
              avatarUrl={patient.avatar_url}
              alt=""
              fallback={initials}
            />
          ) : (
            initials
          )}
        </div>

        <div className={styles.nameBlock}>
          <h2 className={styles.name}>
            {patient.first_name} {patient.last_name}
          </h2>
          <div className={styles.profileMeta}>
            {patient.display_id && (
              <span className={styles.displayId}>{patient.display_id}</span>
            )}
            <span className={`${styles.statusChip} ${patient.is_active ? styles.active : styles.inactive}`}>
              {patient.is_active ? t('patientProfile.active') : t('patientProfile.inactive')}
            </span>
          </div>
        </div>

        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>

      <div className={styles.metaBar}>
        <MetaItem
          icon={<Icon name="email" size={16} />}
          label={t('patientProfile.emailLabel')}
          value={
            patient.email ??
            (patient.registration_pending ? t('patientProfile.notRegisteredYet') : '—')
          }
        />
        <MetaItem
          icon={<Icon name="calendar" size={16} />}
          label={t('patientProfile.birthDateLabel')}
          value={formatDate(patient.birth_date)}
        />
        <MetaItem
          icon={<Icon name="person" size={16} />}
          label={t('patientProfile.genderLabel')}
          value={formatGender(patient.gender)}
        />
        <LeadIdRow patient={patient} />
      </div>
    </header>
  );
}

function MetaItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className={styles.metaItem} title={`${label}: ${value}`}>
      <span className={styles.metaIcon} aria-hidden="true">{icon}</span>
      <span className={styles.srOnly}>{label}</span>
      <span className={styles.metaValue}>{value}</span>
    </div>
  );
}

function LeadIdRow({ patient }: { patient: Patient }) {
  const { t } = useTranslation();
  const updateMutation = useUpdatePatient(patient.id);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(patient.lead_id ?? '');
  const editButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(patient.lead_id ?? '');
    }
  }, [patient.lead_id, isEditing]);

  async function handleSave() {
    const normalized = draft.trim();
    const next = normalized ? normalized : null;
    if (next === (patient.lead_id ?? null)) {
      setIsEditing(false);
      requestAnimationFrame(() => editButtonRef.current?.focus());
      return;
    }
    try {
      await updateMutation.mutateAsync({ lead_id: next });
      setIsEditing(false);
      requestAnimationFrame(() => editButtonRef.current?.focus());
    } catch {
      // surfaced via mutation.error
    }
  }

  function handleCancel() {
    setDraft(patient.lead_id ?? '');
    setIsEditing(false);
    updateMutation.reset();
    requestAnimationFrame(() => editButtonRef.current?.focus());
  }

  const errorMessage =
    updateMutation.error instanceof Error
      ? updateMutation.error.message
      : updateMutation.error
        ? t('patientProfile.leadIdSaveFailed')
        : null;

  return (
    <div className={styles.metaItem}>
      <span className={styles.metaIcon} aria-hidden="true">
        <Icon name="link" size={16} />
      </span>
      <span className={styles.metaLabel}>{t('patientProfile.leadIdLabel')}</span>
      <div className={styles.leadIdBody}>
        {isEditing ? (
          <div className={styles.leadIdEditor}>
            <input
              className={styles.leadIdInput}
              type="text"
              aria-label={t('patientProfile.leadIdLabel')}
              aria-invalid={Boolean(errorMessage)}
              value={draft}
              maxLength={64}
              autoFocus
              placeholder={t('patientProfile.leadIdPlaceholder')}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSave();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  handleCancel();
                }
              }}
              disabled={updateMutation.isPending}
            />
            <div className={styles.leadIdActions}>
              <button
                type="button"
                className={styles.leadIdSave}
                onClick={() => void handleSave()}
                disabled={updateMutation.isPending}
              >
                {t('patientProfile.leadIdSave')}
              </button>
              <button
                type="button"
                className={styles.leadIdCancel}
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                {t('patientProfile.leadIdCancel')}
              </button>
            </div>
            {errorMessage && <div className={styles.leadIdError} role="alert">{errorMessage}</div>}
          </div>
        ) : (
          <button
            type="button"
            ref={editButtonRef}
            className={styles.leadIdDisplay}
            onClick={() => setIsEditing(true)}
            title={t('patientProfile.leadIdEdit')}
          >
            <span className={patient.lead_id ? styles.metaValue : styles.leadIdEmpty}>
              {patient.lead_id ?? t('patientProfile.leadIdEmpty')}
            </span>
            <Icon name="edit" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
